import { inject, Injectable } from '@angular/core';
import { concatMap, Observable, ReplaySubject } from 'rxjs';
import { CmsApiService } from '../api/cms-api.service';
import { filter, map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { GameAttribute } from './game-attribute';
import { FavoriteGamesService } from './favorite-games.service';
import { LastPlayedGamesService } from './last-played-games.service';
import { CmsContentMapperService } from '../cms-content-mapper.service';
import { GameProvidersService } from './game-providers.service';
import { HostRequestsInterceptor } from '../../interceptors/host-requests.interceptor';
import { FiltersService } from '../filters.service';
import { GameCategory } from './game-category';
import { CheckWebpService } from '../check-webp.service';
import { ContentUpdaterService } from '../content-updater/content-updater.service';

interface GamesList {
  gameList: any[];
  attrList: any[];
  countTotal: number;
  countWithoutSearch?: number;
  providerList?: any[];
}

@Injectable({
  providedIn: 'root',
})
export class GamesService {

  private _providers = inject(GameProvidersService);
  private _api = inject(CmsApiService);
  private _favorite = inject(FavoriteGamesService);
  private _lastPlayed = inject(LastPlayedGamesService);
  private _cmsMapper = inject(CmsContentMapperService);
  private _hostInterceptor = inject(HostRequestsInterceptor);
  private _filters = inject(FiltersService);
  private _checkWebp = inject(CheckWebpService);
  private _contentUpdate = inject(ContentUpdaterService);

  /**
   * SS host for game images
   */
  private _ssImageHost = 'https://cdn.softswiss.net/i/s3/';

  constructor() {
    this._favorite.gamesService = this;
    this._lastPlayed.gamesService = this;
  }

  /**
   * Use this function for loading games from cms
   *
   * @param params
   * @param cache
   * @param advanceGameList
   */
  list(params: any = {}, cache: boolean = true, advanceGameList = []): ReplaySubject<any> {
    return this._providers.list$.pipe(
      concatMap(() => cache ? this._api.gameList({
        ...params,
      }) : this._api.gameListNoCache({
        ...params,
      })),
      map(response => this._gameListResponseMapper(response.data, advanceGameList)),
    ) as ReplaySubject<any>;
  }

  /**
   * Use this function for loading game content from cms
   * @param params
   */
  item(params: object = {}): Observable<any> {
    return this._contentUpdate.registerContent('/game/content/item', params, () =>
      this._api.gameContent(params).pipe(
        map(res => this._cmsMapper.mapCmsTemplate(res.data.list ? { content: res.data.list[0]?.content } : null)),
      ));
  }

  /**
   * Prepare game list response for using in frontend
   *
   * @param data
   * @param advanceGameList
   * @private
   */
  private _gameListResponseMapper(data: GamesList, advanceGameList = []): GamesList {
    return {
      gameList: this.gameListMapper(data && data.gameList, advanceGameList),
      attrList: (data && data.attrList) || [],
      providerList: (data && data.providerList) || [],
      countTotal: data.countTotal,
    };
  }

  /**
   * Prepare game list for using in frontend
   *
   * @param list
   * @param advanceGameList
   * @private
   */
  public gameListMapper(list: Array<any>, advanceGameList = []): Array<any> {
    if (!list) {
      return [];
    }

    const mappedGameList = list
      .filter(game => game.externalData)
      .map(game => {
        game.attrTranslate = game.attrTranslate || [];
        const image: ResolvedImage = this._resolveImageSrc(game);

        return {
          ...game,
          provider: this._providers.getById(game.providerId),
          imgSrc: image.image,
          altImgSrc: image.alternativeImage,
          hot: (game.attrTranslate || []).some((attribute: any) => attribute.attrId === GameAttribute.HOT),
          bitcoin: (game.attrTranslate || []).some((attribute: any) => attribute.attrId === GameAttribute.BITCOIN),
          new: (game.attrTranslate || []).some((attribute: any) => attribute.attrId === GameAttribute.NEW),
          nodemo: (game.attrTranslate || []).some((attribute: any) => attribute.attrId === GameAttribute.NODEMO),
          flash: (game.attrTranslate || []).some((attribute: any) => attribute.attrId === GameAttribute.FLASH),
          dailyDrops: (game.attrTranslate || []).some((attribute: any) => attribute.attrId === GameAttribute.DAILY_DROPS),
          preRelease: (game.attrTranslate || []).some((attribute: any) => attribute.attrId === GameAttribute.PRE_RELEASE),
          jackpot: (game.attrTranslate || []).some((attribute: any) => attribute.attrId === GameAttribute.JACKPOT),
          bonanzaJackpot: (game.categoryTranslate || []).some((category: any) => category.categoryId === GameCategory.BONANZA_JACKPOT),
          mystery: (game.attrTranslate || []).some(attribute => attribute.attrId === GameAttribute.MYSTERY_DROP),
        };
      });

    if (advanceGameList?.length && mappedGameList.some(mg => advanceGameList.some(g => g.slug === mg.slug))) {
      return this._filters.sortByArrayOrderAndFillArray(advanceGameList.map(g => g.slug), 'slug', mappedGameList);
    }

    return [...advanceGameList, ...mappedGameList.slice(0, mappedGameList?.length - advanceGameList.length)];
  }

  /**
   * Returns image src for provided game
   *
   * @param game
   * @private
   */
  private _resolveImageSrc(game: any): ResolvedImage {
    /**
     * Uses for detecting which image use (From CMS or from SS)
     */
    const extensionList = ['.jpg', '.jpeg', '.bmp', '.gif', '.png', '.svg', '.webp'];

    /**
     * Regexp for checking file extension
     */
    const regexp = new RegExp('(' + extensionList.join('|').replace(/\./g, '\\.') + ')(\\?.*)?$');

    const isCmsImagePriority = game.imgSrc && regexp.test(game.imgSrc);
    const cmsImage: string = environment.assets_host + game.imgSrc;
    const ssImage: string = this._ssImageHost + game.externalId + (this._checkWebp.isWebpSupported() ? '.webp' : '.png');


    if (isCmsImagePriority) {
      return { image: this._hostInterceptor.overrideUrl(cmsImage), alternativeImage: ssImage };
    } else {
      return { image: ssImage, alternativeImage: cmsImage };
    }

  }

  /**
   * Get game by identifier from SS
   * @param identifier
   */
  public getGameByExternalId(identifier: string): Observable<any> {
    return this.list({
      'external_id[]': identifier,
      offset: 0,
      limit: 25,
    }).pipe(
      filter(data => data.gameList[0]),
      map(data => data.gameList[0]),
    );
  }

}


interface ResolvedImage {
  image: string;
  alternativeImage: string;
}
